import type { TimeRange } from '@/components/data-truth/TimeRangePicker'

/**
 * The six preset windows every metrics range endpoint records distinct history
 * for — all the picker offers except `custom` (admind's shared windowSpec
 * whitelist: 15m/1h/6h/24h/7d/30d, each with its own real resolution). No panel
 * snaps a picker value to a "nearest" supported window any more; the store
 * serves them all.
 */
export type PresetWindow = '15m' | '1h' | '6h' | '24h' | '7d' | '30d'

/** Back-compat alias for the preset windows the range endpoints serve. */
export type ApiWindow = PresetWindow

/**
 * A resolved range selection threaded to every data panel — either a preset
 * window or a custom [start,end] span in unix seconds. This module owns the
 * mapping: every hook turns a WindowSel into the endpoint's
 * `window=`/`&start=&end=` args and its React-Query key, so charts, KPI, the
 * verbs table, and customers all honor the same selection with no per-panel
 * re-mapping.
 */
export type WindowSel =
  | { kind: 'preset'; window: PresetWindow }
  | { kind: 'custom'; start: number; end: number }

/** The compare-overlay mode the ContextBar drives: off, or one of three shifts
 * applied to the current window to fetch + overlay a prior period as a dashed,
 * half-opacity series. */
export type CompareMode = 'off' | 'prev' | 'yesterday' | 'lastweek'

/** Wrap a preset window as a selection. */
export function presetSel(window: PresetWindow): WindowSel {
  return { kind: 'preset', window }
}

/**
 * Map a picker TimeRange (which includes `custom`) to a WindowSel for a surface
 * WITHOUT a custom-bounds picker — the service-page shell, whose range dropdown
 * offers `custom` but carries no start/end. Presets pass straight through (the
 * store records all six); `custom` falls back to 1h since there are no bounds to
 * honor here. The Metrics portal, which DOES carry custom bounds in its URL,
 * builds its WindowSel directly and never routes through this.
 */
export function rangeToSel(range: TimeRange): WindowSel {
  return range === 'custom' ? { kind: 'preset', window: '1h' } : { kind: 'preset', window: range }
}

/** The URL/query params for a selection: `{window}` for a preset, or
 * `{window:'custom', start, end}` for a custom span. */
export function windowParams(sel: WindowSel): Record<string, string> {
  return sel.kind === 'custom'
    ? { window: 'custom', start: String(sel.start), end: String(sel.end) }
    : { window: sel.window }
}

/** A stable cache-key / csv-name fragment for a selection (same value ⇒ same
 * React-Query key, so a range change refetches and an unchanged one doesn't). */
export function windowKey(sel: WindowSel): string {
  return sel.kind === 'custom' ? `custom:${sel.start}-${sel.end}` : sel.window
}

/** A short human label for the served window (source chips / captions). */
export function windowLabel(sel: WindowSel): string {
  return sel.kind === 'custom' ? 'custom range' : sel.window
}

/** The query step each preset resolves to server-side (mirrors admind's
 * windowSpec) — shown on the source chip so the resolution is honest. */
const PRESET_STEP: Record<PresetWindow, string> = {
  '15m': '15s step',
  '1h': '30s step',
  '6h': '2m step',
  '24h': '5m step',
  '7d': '30m step',
  '30d': '2h step',
}

/** The resolution caption for a selection. A custom span's step is derived
 * server-side (span/240, floored at 15s), so it is labelled generically. */
export function windowStepLabel(sel: WindowSel): string {
  return sel.kind === 'preset' ? PRESET_STEP[sel.window] : 'auto step'
}

/** The shift (seconds) a compare mode applies to the current window to fetch the
 * prior period: previous period = the window's own span; yesterday = 24h; last
 * week = 7d. `off` (or an unknown mode) is 0 — no overlay. */
export function compareShiftSeconds(mode: CompareMode, spanSeconds: number): number {
  switch (mode) {
    case 'prev':
      return spanSeconds
    case 'yesterday':
      return 86_400
    case 'lastweek':
      return 604_800
    default:
      return 0
  }
}

/** A short label for a compare mode (ContextBar chip + overlay caption). */
export const COMPARE_LABEL: Record<CompareMode, string> = {
  off: 'off',
  prev: 'previous period',
  yesterday: 'same time yesterday',
  lastweek: 'same time last week',
}
